<!-- eslint-disable max-len -->
<template>
  <div
    class="report"
    @click="goReport"
    :class="{ 'no-habilitado': !habilitado }"
  >
    <div class="flex flex-column md:flex-row align-items-stretch gap-3">
      <!-- Image -->
      <div class="report-image flex align-items-center">
        <img :src="image" />
      </div>

      <!-- Name and description -->
      <div class="report-details flex flex-column">
        <div class="name mb-2">
          {{ titulo }}
        </div>
        <div class="description">{{ descripcion }}</div>
      </div>

      <!-- Action buttons -->
      <div class="actions align-items-start md:align-items-end">
        <div
          class="action-icons flex justify-content-end align-items-center gap-2"
        >
          <template v-if="!loading">
            <i
              v-if="false && userRol == 'Admin'"
              @click="getReportDetails('handleShowFiles')"
              class="pi pi-upload mr-1"
              v-tooltip.top="'Gestionar archivos'"
            ></i>
            <i
              v-if="showConfigurationAction"
              @click="getReportDetails('handleUpdate')"
              class="pi pi-cog"
              v-tooltip.top="'Editar informe'"
            ></i>
            <i
              v-if="showManageUsersAction || gestionaUsuarios"
              @click="getReportDetails('handleShowUsers')"
              class="pi pi-users"
              v-tooltip.top="'Gestionar usuarios'"
            ></i>
            <i
              :class="[favouriteIcon]"
              @click="onToggleFavourite(favouriteIcon)"
              v-tooltip.top="{
                value:
                  favouriteIcon == 'pi pi-star-fill'
                    ? 'Quitar de favoritos'
                    : 'Agregar a favoritos',
              }"
              class="text-yellow-500"
            ></i>
          </template>
          <template v-else>
            <i class="pi pi-spin pi-spinner"></i>
          </template>
        </div>
        <div class="flex flex-wrap gap-1 align-items-end justify-content-end">
          <div
            class="status text-xs px-2 py-1 border-round-lg text-white"
            :class="status"
          >
            {{ statusText }}
          </div>
          <div
            v-if="!habilitado"
            class="status private text-xs px-2 py-1 border-round-lg text-white"
          >
            Inhabilitado
          </div>
          <div
            v-if="!visible"
            class="status invisible text-xs px-2 py-1 border-round-lg text-white"
          >
            No visible
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useAuthorization } from "@sttedra_ui/framework";
import { useRouter } from "vue-router";

import { checkTokenExpired } from "@/composables/_commons/auth.js";

import {
  userReportDetails,
  userReportAdditionalDetails,
  userReportFileList,
  addFavorite,
  deleteFavorite,
} from "@/api/reports/reports.api.js";
import { reportTypes } from "@/composables/_commons/enums.js";

const selectedImpersonation = ref(null);
const loading = ref(false);

// #region Props, Emits
const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  alias: {
    type: String,
    required: true,
  },
  titulo: {
    type: String,
    required: true,
  },
  status: {
    type: String,
    required: true,
  },
  descripcion: {
    type: String,
    required: false,
  },
  usuarios: {
    type: Array,
    required: false,
  },
  tipoInforme: {
    type: String,
    required: true,
  },
  gestionaUsuarios: {
    type: Boolean,
    required: false,
  },
  actualizaArchivos: {
    type: Boolean,
    required: false,
  },
  url: {
    type: String,
    required: false,
  },
  tieneLandingPage: {
    type: Boolean,
    required: false,
  },
  habilitado: {
    type: Boolean,
    required: false,
  },
  esFavorito: {
    type: Boolean,
    required: false,
  },
  publicoPrivado: {
    type: Boolean,
    required: false,
  },
  visible: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "onShowUsers",
  "onUpdate",
  "onShowFiles",
  "onToggleFavorite",
]);
// #endregion

// #region  Params, Ref, Reactives, Computed
const router = useRouter();
const statusText = computed(() => {
  if (props.publicoPrivado) {
    return "Interno";
  } else {
    return props.status == "public" ? "Público" : "Privado";
  }
});

const image = computed(() => {
  switch (props.tipoInforme) {
    case reportTypes.PowerBI:
      return require("@/assets/images/report-types/report.png");

    case reportTypes.Tabla:
      return require("@/assets/images/report-types/datasource.png");

    case reportTypes.Grafico:
      return require("@/assets/images/report-types/chart.png");

    case reportTypes.Mapa:
      return require("@/assets/images/report-types/map.png");

    case reportTypes.URL:
      return require("@/assets/images/report-types/url.png");

    default:
      return require("@/assets/images/report-types/report.png");
  }
});

const showConfigurationAction = computed(() => {
  if (selectedImpersonation.value) {
    if (reportTypes.PowerBI === props.tipoInforme) {
      return (
        selectedImpersonation.value.esAdministrador || props.actualizaArchivos
      );
    } else {
      return false;
    }
  } else {
    if (userRol === "Admin") {
      return true;
    } else {
      return props.actualizaArchivos;
    }
  }
});

const showManageUsersAction = computed(() => {
  if (props.gestionaUsuarios) {
    return true;
  } else {
    return selectedImpersonation.value
      ? selectedImpersonation.value.esAdministrador
      : userRol === "Admin";
  }
});

const { modelRef: authorizationModelRef } = useAuthorization();
const userRol = authorizationModelRef.value.roles[0];
const favouriteIcon = ref("pi pi-star");
// #endregion

// #region Functions
async function getReportDetails(action) {
  loading.value = true;

  let reportFiles = null;
  let reportDetails = null;
  let additionalDetails = null;

  if (action == "handleShowFiles") {
    reportFiles = await userReportFileList({ id: props.id });
  } else {
    const response = await userReportDetails(props.alias);

    if (response.status != 200) {
      loading.value = false;
      return;
    }

    reportDetails = response.data;

    additionalDetails = await userReportAdditionalDetails(props.alias);
  }

  loading.value = false;

  switch (action) {
    case "handleShowUsers":
      emit("onShowUsers", {
        alias: props.alias,
        idInforme: props.id,
        titulo: reportDetails.titulo,
        tieneRoles: reportDetails.tieneRoles,
        rolesInforme: reportDetails.rolesInforme,
        accesosInforme: reportDetails.accesosInforme,
        gestionaUsuarios: props.gestionaUsuarios,
      });
      break;

    case "handleUpdate":
      reportDetails = { ...reportDetails, ...additionalDetails };
      emit("onUpdate", reportDetails);
      break;

    case "handleShowFiles":
      emit("onShowFiles", reportFiles);
      break;
  }
}

async function goReport($event) {
  if ($event.target.tagName === "I") return;

  const expired = await checkTokenExpired();

  if (expired) {
    router.push({ name: "Login" });
    return;
  }

  if (props.tipoInforme == "Url" && !props.tieneLandingPage) {
    window.open(props.url, "_blank");
    return;
  }

  let pathname = props.status == "public" ? "/public" : "/reports";

  pathname += `/${props.alias}`;

  window.open(pathname, "_blank");
}

async function onToggleFavourite() {
  if (favouriteIcon.value == "pi pi-star") {
    favouriteIcon.value = "pi pi-star-fill";
  } else {
    favouriteIcon.value = "pi pi-star";
  }

  const email = selectedImpersonation.value
    ? selectedImpersonation.value.email
    : authorizationModelRef.value.email;

  if (favouriteIcon.value == "pi pi-star-fill") {
    await addFavorite({
      idInforme: props.id,
      email,
    });
  } else {
    await deleteFavorite({
      idInforme: props.id,
      email,
    });
  }

  emit("onToggleFavorite");
}
// #endregion

// #region Effects, Hooks
onMounted(() => {
  if (props.esFavorito) {
    favouriteIcon.value = "pi pi-star-fill";
  }

  const impersonationToken = localStorage.getItem("impersonationToken");

  if (impersonationToken) {
    selectedImpersonation.value = JSON.parse(impersonationToken);
  }
});
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.report {
  background-color: #f7f7ef;
  padding: 1rem;
  border-radius: 12px;
  transition: 0.5s all ease;

  &.no-habilitado {
    background-color: #ffe2e2;
  }

  &:hover {
    cursor: pointer;
  }

  .report-image {
    min-width: 80px;
    width: 80px;
    filter: opacity(0.8);

    img {
      width: 100%;
    }
  }

  .report-details {
    flex-grow: 1;

    .name {
      font-size: 18px;
      line-height: 20px;
      font-weight: 700;
    }

    .description {
      color: #333333;
      font-size: 1em;
    }
  }

  .status {
    white-space: nowrap;
    font-size: 1.2rem;
    font-weight: bold;

    &.public {
      background-color: green;
    }

    &.private {
      background-color: #dc3545;
    }

    &.invisible {
      background-color: gray;
    }
  }

  .actions {
    min-height: 100px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}

@media (min-width: $mobile-min-width) and (max-width: $mobile-max-width) {
  .report {
    .actions {
      flex-direction: row;
      min-height: unset;
    }
  }
}
</style>
